import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../../components/Head/SeoTags";
import { CleanupMock } from "../../../components/Mockups/CleanupMock";

const Cleanup = () => {
  const { t } = useTranslation();

  return (
    <>
      <SeoTags
        title={t("featurePages.cleanup.seo.title")}
        description={t("featurePages.cleanup.seo.description")}
        keywords={t("featurePages.cleanup.seo.keywords")}
      />

      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("featurePages.cleanup.seo.title"),
            description: t("featurePages.cleanup.seo.description"),
            url: "https://onbookmarks.com/features/cleanup/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "One-click dead link scan",
                "Duplicate bookmark detection",
                "Wayback Machine recovery for broken links",
                "Conservative flagging with no false alarms",
                "Encrypted bookmarks are never scanned"
              ],
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Library cleanup is free"
              }
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://onbookmarks.com"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Features",
                  item: "https://onbookmarks.com/features/"
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Library Cleanup",
                  item: "https://onbookmarks.com/features/cleanup/"
                }
              ]
            }
          }),
        }}
      />

      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    {t("featurePages.cleanup.hero.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  {t("featurePages.cleanup.hero.title")}
                </h1>
                <p className="lead">
                  {t("featurePages.cleanup.hero.description")}
                </p>
                <div>
                  <Link
                    to={`${process.env.REACT_APP_APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    {t("featurePages.cleanup.hero.cta")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <CleanupMock />
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.cleanup.problem.title")}
              </h2>
              <p className="lead text-muted">
                {t("featurePages.cleanup.problem.description")}
              </p>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-link-45deg fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.cleanup.problem.card1.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.cleanup.problem.card1.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-files fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.cleanup.problem.card2.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.cleanup.problem.card2.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-trash fs-2 text-danger mb-3"></i>
                  <h3 className="h5 mb-3">
                    {t("featurePages.cleanup.problem.card3.title")}
                  </h3>
                  <p className="text-muted mb-0">
                    {t("featurePages.cleanup.problem.card3.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="mb-3">
                <span className="badge bg-purple text-white px-3 py-2">
                  {t("featurePages.cleanup.solution.badge")}
                </span>
              </div>
              <CleanupMock />
            </div>
            <div className="col-lg-6">
              <h2 className="h1 fw-bold mb-4">
                {t("featurePages.cleanup.solution.title")}
              </h2>
              <p className="lead mb-4">
                {t("featurePages.cleanup.solution.description")}
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.cleanup.solution.benefit1.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.cleanup.solution.benefit1.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.cleanup.solution.benefit2.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.cleanup.solution.benefit2.description")}
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-shield-lock text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">
                      {t("featurePages.cleanup.solution.benefit3.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("featurePages.cleanup.solution.benefit3.description")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.cleanup.howItWorks.title")}
            </h2>
            <p className="lead text-muted">
              {t("featurePages.cleanup.howItWorks.description")}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">1</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.cleanup.howItWorks.step1.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.cleanup.howItWorks.step1.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">2</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.cleanup.howItWorks.step2.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.cleanup.howItWorks.step2.description")}
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">3</span>
                </div>
                <h3 className="h5 mb-3">
                  {t("featurePages.cleanup.howItWorks.step3.title")}
                </h3>
                <p className="text-muted">
                  {t("featurePages.cleanup.howItWorks.step3.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center text-white">
            <h2 className="h1 fw-bold mb-4">
              {t("featurePages.cleanup.cta.title")}
            </h2>
            <p className="lead mb-4">
              {t("featurePages.cleanup.cta.description")}
            </p>
            <Link
              to={`${process.env.REACT_APP_APP_URL}/register`}
              className="btn btn-light btn-lg px-5"
            >
              {t("featurePages.cleanup.cta.button")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Cleanup;
